/**
 * Shared Sentry bootstrap for the four web surfaces (audit finding N5).
 *
 * All four panels previously called `Sentry.init` with a byte-identical
 * options object, differing only in the `app.name` tag, and each hardcoded
 * the DSN. That is four places to change and four places to forget — and
 * what had already been forgotten was a masking policy.
 *
 * Three of these panels render patient records, so Session Replay is
 * recording sessions on a PHI surface and shipping them to a third party.
 * Sentry's SDK defaults (`maskAllText`, `blockAllMedia`) do cover that, so
 * there was no demonstrated leak — but a PHI surface must not depend on an
 * IMPLICIT third-party default that a version bump could change, with
 * nothing in our own code stating the intent. Everything below is therefore
 * passed explicitly, even where it matches today's default: the point is
 * that a future SDK release cannot quietly widen what we capture, and a
 * reader can see the policy without going to Sentry's changelog.
 *
 * What is NOT solved here: whether a DPA with Sentry is in place. The DSN is
 * EU-region (`ingest.de.sentry.io`), which is the right call for GDPR, but
 * region alone is not a lawful basis for processing health-adjacent data.
 */
import * as Sentry from "@sentry/react";

/**
 * Query/path parameters that identify a patient, a consultation or a tenant.
 * `browserTracingIntegration` sends URLs as-is, so without scrubbing these
 * travel to Sentry inside transaction names and breadcrumbs even though the
 * replay itself is masked.
 */
const SENSITIVE_PARAMS = [
  "consultationId",
  "bookingId",
  "patientName",
  "patientId",
  "pharmacyId",
  "chainId",
  "uid",
  "email",
  "token",
  "oobCode",
];

/**
 * Firestore document ids and Firebase uids appear as bare PATH segments
 * (`/consultations/aB3xQ9zLmN2pR7wK`), where there is no key to match on.
 * Any segment that looks like an opaque id is replaced wholesale.
 *
 * Three deliberate constraints, because over-scrubbing is its own failure —
 * if route names were replaced too, every transaction would collapse into
 * one unreadable bucket and the tracing data would be worth nothing:
 *
 *   - 16+ characters. Firestore auto-ids are 20, Firebase uids are 28.
 *   - NO hyphens or underscores. This is what keeps `pharmacy-admin-settings`
 *     intact; an earlier version of this pattern included them in the class
 *     and silently ate every long kebab-case route.
 *   - At least one digit. Firestore ids and uids are drawn from a
 *     mixed-alphanumeric alphabet and effectively always contain one, while a
 *     long all-letter segment is a route name (`consultationdetail`).
 */
const OPAQUE_SEGMENT = /^(?=.*[0-9])[A-Za-z0-9]{16,}$/;

export function scrubUrl(raw: string): string {
  if (!raw) return raw;

  // Sentry hands us absolute URLs, relative paths, and occasionally values
  // that are not URLs at all. A parse failure must never throw out of
  // beforeSend — that would drop the event entirely and blind us.
  try {
    const hasScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(raw);
    // `globalThis.URL` rather than bare `URL`: the booking app's ESLint config
    // declares no browser globals, so a bare reference is a no-undef error
    // there. Kept identical in both copies of this file — see the drift note.
    const url = new globalThis.URL(
      raw,
      hasScheme ? undefined : "https://placeholder.invalid",
    );

    for (const key of SENSITIVE_PARAMS) {
      if (url.searchParams.has(key)) url.searchParams.set(key, "[redacted]");
    }

    url.pathname = url.pathname
      .split("/")
      .map((segment) => (OPAQUE_SEGMENT.test(segment) ? "[id]" : segment))
      .join("/");

    // Fragments are never needed for diagnosis and can carry anything.
    url.hash = "";

    return hasScheme ? url.toString() : `${url.pathname}${url.search}`;
  } catch {
    return raw;
  }
}

/**
 * The parts of an event this scrubber touches. Declared structurally rather
 * than as `Sentry.ErrorEvent | Sentry.TransactionEvent`: `TransactionEvent`
 * lives in `@sentry/core` and is not re-exported by `@sentry/react`, so
 * naming it couples this file to the SDK's internal export layout. Matching
 * on shape keeps it compiling across SDK versions, which is the whole point
 * of pinning the masking policy in our own code.
 */
interface ScrubbableEvent {
  request?: {
    url?: string;
    headers?: Record<string, string | undefined>;
  };
  breadcrumbs?: Array<{ data?: Record<string, unknown> }>;
}

export function scrubEventUrls<T extends ScrubbableEvent>(event: T): T {
  if (event.request?.url) {
    event.request.url = scrubUrl(event.request.url);
  }
  // Sentry copies the URL into the request headers on some transports.
  if (event.request?.headers?.Referer) {
    event.request.headers.Referer = scrubUrl(event.request.headers.Referer);
  }
  if (event.breadcrumbs) {
    for (const crumb of event.breadcrumbs) {
      if (typeof crumb.data?.url === "string") {
        crumb.data.url = scrubUrl(crumb.data.url);
      }
      // Navigation crumbs carry the route on `from`/`to`, not `url`.
      if (typeof crumb.data?.from === "string") {
        crumb.data.from = scrubUrl(crumb.data.from);
      }
      if (typeof crumb.data?.to === "string") {
        crumb.data.to = scrubUrl(crumb.data.to);
      }
    }
  }
  return event;
}

export interface InitSentryOptions {
  /** Value for the `app.name` tag, e.g. "chain-admin". */
  appName: string;
  /**
   * Vite's `import.meta.env`. Passed in rather than read directly so this
   * module stays testable and does not assume a bundler.
   */
  env: Record<string, string | undefined | boolean>;
}

export function initSentry({ appName, env }: InitSentryOptions): void {
  const isDev = env.DEV === true || env.DEV === "true";
  const dsn =
    (env.VITE_SENTRY_DSN as string | undefined) ??
    "https://bea64bd8f45e3755b00dbf648311b00e@o4510440882503680.ingest.de.sentry.io/4511734099345488";

  Sentry.init({
    dsn,
    environment: (env.MODE as string | undefined) ?? "production",
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // All three stated explicitly. `maskAllText` and `blockAllMedia`
        // match the SDK default; `maskAllInputs` does too, and is named
        // because a patient's name and DOB are typed into inputs, which is
        // the single worst thing that could be captured here.
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
    ],
    // Was 1.0 in production on all four panels. 100% of transactions is not a
    // defensible default for a PHI surface: every transaction is another URL
    // and another set of breadcrumbs leaving the building. Full rate in dev,
    // where there is no patient data and the traces are what you want.
    tracesSampleRate: isDev ? 1.0 : 0.1,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // `sendDefaultPii` defaults to false; stated because turning it on would
    // attach IP addresses and user agents to every event.
    sendDefaultPii: false,
    beforeSend: scrubEventUrls,
    beforeSendTransaction: scrubEventUrls,
    initialScope: {
      tags: { "app.name": appName },
    },
  });
}
